import { useState, useContext } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
  Paper,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import TemplateLib from '@common/libs/interview-template-lib'
import ActiveEditorContext from '~/providers/ActiveEditorContext'
import FiltersContext from '~/providers/FiltersContext'
import ImportExportIcon from '@material-ui/icons/ImportExport'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

export default function ImportVariables() {
  const { activeEditor } = useContext(ActiveEditorContext)
  const { filtersData } = useContext(FiltersContext)

  // Configure Quill modules to preserve formatting when pasting from Word
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ color: [] }, { background: [] }],
    ],
    clipboard: {
      // This is the key setting to preserve formatting when pasting from Word
      matchVisual: false,
    },
  }

  const [open, setOpen] = useState(false)
  const [textContent, setTextContent] = useState('')
  const [extractedVariables, setExtractedVariables] = useState([])
  const [selectedVariableIds, setSelectedVariableIds] = useState([])
  const [isExtracting, setIsExtracting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importComplete, setImportComplete] = useState(false)
  const [updatedText, setUpdatedText] = useState('')
  const [variableMapping, setVariableMapping] = useState({})
  const [createdSelectedVariables, setCreatedSelectedVariables] = useState([])
  const [createdDependentVariables, setCreatedDependentVariables] = useState([])

  const handleClickOpen = () => {
    setOpen(true)
    setTextContent('')
    setExtractedVariables([])
    setSelectedVariableIds([])
    setImportComplete(false)
    setUpdatedText('')
    setVariableMapping({})
    setCreatedSelectedVariables([])
    setCreatedDependentVariables([])
  }

  const handleClose = () => {
    setOpen(false)
  }

  const extractVariables = () => {
    if (!textContent.trim()) {
      toast.error('Veuillez entrer du texte contenant des variables')
      return
    }

    // Get plain text for regex processing, but keep HTML for display

    setIsExtracting(true)

    try {
      // Regex to match variable patterns like bool_123, choice_456, etc.
      const regex =
        /(bool|choice|text|number|date|object|unisign|multisign|textformat|composable|multi|list|array|multiarray)_(\d+)/g
      let match
      const foundVariables = new Map()

      while ((match = regex.exec(textContent)) !== null) {
        const variableType = match[1]
        const variableId = match[2]
        const fullMatch = match[0]

        if (!foundVariables.has(fullMatch)) {
          foundVariables.set(fullMatch, {
            type: variableType,
            id: variableId,
          })
        }
      }

      const variablesArray = Array.from(foundVariables.values())

      if (variablesArray.length === 0) {
        toast.warning("Aucune variable n'a été trouvée dans le texte")
      } else {
        setExtractedVariables(variablesArray)
        setSelectedVariableIds(variablesArray.map((v) => v.id))
        toast.success(`${variablesArray.length} variables trouvées`)
      }
    } catch (error) {
      console.error('Error extracting variables:', error)
      toast.error("Erreur lors de l'extraction des variables")
    } finally {
      setIsExtracting(false)
    }
  }

  const handleToggleVariable = (variable) => {
    setSelectedVariableIds((prev) => {
      if (prev.includes(variable.id)) {
        return prev.filter((v) => v !== variable.id)
      } else {
        return [...prev, variable.id]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedVariableIds.length === extractedVariables.length) {
      setSelectedVariableIds([])
    } else {
      setSelectedVariableIds(extractedVariables.map((v) => v.id))
    }
  }

  const importVariables = async () => {
    if (selectedVariableIds.length === 0) {
      toast.error('Veuillez sélectionner au moins une variable à importer')
      return
    }

    setIsImporting(true)

    try {
      const projectId = activeEditor.data.id
      const domain = filtersData.site.domain
      console.log('selectedVariableIds', selectedVariableIds)

      // Get all variables from the current project
      const projectVariables = await TemplateLib.getVariablesFromProject(projectId, domain)
      // Get the variable from other projects
      const extractedVariables = await TemplateLib.getVariables(selectedVariableIds)
      console.log('extractedVariables', extractedVariables)
      // Filter selected variables
      const variablesToImport = extractedVariables.filter((v) =>
        selectedVariableIds.includes(String(v.id))
      )
      console.log('variablesToImport', variablesToImport)

      let importedCount = 0
      const createdSelected = []
      const createdDependents = []

      // Process each variable
      for (const variable of variablesToImport) {
        try {
          // Check if this variable already exists in the project
          const existingVariable = projectVariables.find(
            (v) => v.name === variable.name || v.id === String(variable.id)
          )

          if (!existingVariable) {
            // Create a copy of the variable in the current project
            const newVariable = {
              ...variable,
              project: projectId,
              order_in_group: projectVariables.filter((v) => v.group === variable.group).length + 1,
            }

            for (const dependant_variable_id of [
              variable.dependant_variable,
              variable.dependant_variable_2,
              variable.dependant_variable_3,
            ]) {
              if (dependant_variable_id) {
                const dependant_variable = await TemplateLib.getVariable(dependant_variable_id)
                const existingDependantVariable = projectVariables.find(
                  (v) =>
                    v.name === dependant_variable.name || v.id === String(dependant_variable.id)
                )
                // If dependent variable doesn't already exist in project, create it
                if (!existingDependantVariable) {
                  const newDependantVariable = {
                    ...dependant_variable,
                    project: projectId,
                    order_in_group:
                      projectVariables.filter((v) => v.group === dependant_variable.group).length +
                      1,
                  }
                  delete newDependantVariable.id
                  delete newDependantVariable.group
                  await TemplateLib.createVariable(newDependantVariable)
                  createdDependents.push(
                    dependant_variable.name ||
                      `ID ${dependant_variable.id}`
                  )
                  newVariable.dependant_variable = newDependantVariable.id
                } else {
                  newVariable.dependant_variable = existingDependantVariable.id
                }
              }
            }

            // Remove id to create a new variable
            delete newVariable.id
            delete newVariable.group

            // Create the variable
            await TemplateLib.createVariable(newVariable)
            createdSelected.push(variable.name || `ID ${variable.id}`)
            importedCount++
          } else {
            toast.info(`La variable ${variable.name} existe déjà dans ce projet`)
          }
        } catch (error) {
          console.error(`Error importing variable ${variable.name}:`, error)
          toast.error(`Erreur lors de l'importation de la variable ${variable.name}`)
        }
      }

      // Replace variables in the text
      const mapping = {}
      let replacedText = textContent

      // Create mapping from old variable IDs to new ones
      const updatedProjectVariables = await TemplateLib.getVariablesFromProject(projectId, domain)
      variablesToImport.forEach((oldVar) => {
        // Find the newly created variable that matches the old one
        const newVar = updatedProjectVariables.find(
          (v) => v.name === oldVar.name && v.type === oldVar.type
        )

        if (newVar) {
          mapping[oldVar.id] = newVar.id

          // Replace in the text: type_oldId with type_newId
          const oldPattern = new RegExp(`${oldVar.type}_${oldVar.id}`, 'g')
          replacedText = replacedText.replace(oldPattern, `${oldVar.type}_${newVar.id}`)
        }
      })

      // Store the updated text and mapping
      setUpdatedText(replacedText)
      setVariableMapping(mapping)
      setCreatedSelectedVariables(createdSelected)
      setCreatedDependentVariables(createdDependents)
      setImportComplete(true)
      if (importedCount > 0) {
        const depMsg = createdDependents.length
          ? ` (+ ${createdDependents.length} variable${
              createdDependents.length > 1 ? 's' : ''
            } dépendante${createdDependents.length > 1 ? 's' : ''})`
          : ''
        toast.success(
          `${importedCount} variable${importedCount > 1 ? 's' : ''} importée${
            importedCount > 1 ? 's' : ''
          } avec succès${depMsg}`
        )
      } else {
        toast.warning("Aucune nouvelle variable n'a été importée")
      }
    } catch (error) {
      console.error('Error importing variables:', error)
      toast.error("Erreur lors de l'importation des variables")
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleClickOpen}
        style={{
          marginRight: '30px',
          marginBottom: '30px',
        }}
      >
        <ImportExportIcon style={{ marginRight: 10 }} /> Importer des variables
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Importer des variables</DialogTitle>
        <DialogContent>
          {!importComplete ? (
            <div>
              <Typography variant="subtitle1" gutterBottom>
                Collez le texte contenant des variables
              </Typography>
              <div style={{ backgroundColor: 'white', marginBottom: 16 }}>
                <ReactQuill
                  value={textContent}
                  onChange={setTextContent}
                  theme="snow"
                  modules={modules}
                  style={{ minHeight: '200px' }}
                  preserveWhitespace={false}
                />
              </div>
            </div>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Variables importées avec succès. Voici le texte mis à jour avec les nouveaux IDs:
              </Typography>
              <div style={{ backgroundColor: 'white', marginBottom: 16 }}>
                <ReactQuill
                  value={updatedText}
                  onChange={setUpdatedText}
                  theme="snow"
                  modules={modules}
                  id="updated-text"
                  readOnly={true}
                  style={{ minHeight: '200px' }}
                  preserveWhitespace={false}
                />
              </div>
              {(createdSelectedVariables.length > 0 || createdDependentVariables.length > 0) && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Résumé de la création:
                  </Typography>
                  <Paper style={{ maxHeight: 200, overflow: 'auto', padding: 8, marginBottom: 12 }}>
                    <List dense>
                      {createdSelectedVariables.length > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`Variables sélectionnées créées (${createdSelectedVariables.length})`}
                            secondary={createdSelectedVariables.join(', ')}
                          />
                        </ListItem>
                      )}
                      {createdDependentVariables.length > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`Variables dépendantes créées (${createdDependentVariables.length})`}
                            secondary={createdDependentVariables.join(', ')}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  // Use a more sophisticated approach to copy HTML content with Calibri font
                  try {
                    // Get the HTML content from the Quill editor
                    const quillEditor = document.querySelector('#updated-text .ql-editor')
                    if (quillEditor) {
                      let htmlContent = quillEditor.innerHTML

                      // Force Calibri font in the HTML content
                      htmlContent = htmlContent.replace(/<([^>]+)>/g, (match) => {
                        // Don't modify closing tags
                        if (match.startsWith('</')) return match

                        // Add style attribute with Calibri font to all tags
                        if (match.includes('style=')) {
                          return match.replace(
                            /style="([^"]*)"/g,
                            'style="$1; font-family: Calibri, \'Segoe UI\', Roboto, sans-serif !important;"'
                          )
                        } else {
                          return match.replace(
                            />$/,
                            ' style="font-family: Calibri, \'Segoe UI\', Roboto, sans-serif !important;">'
                          )
                        }
                      })

                      // Create a temporary element with Calibri styling
                      const tempElement = document.createElement('div')
                      tempElement.style.fontFamily = "Calibri, 'Segoe UI', Roboto, sans-serif"
                      tempElement.innerHTML = htmlContent
                      document.body.appendChild(tempElement)

                      // Create a blob with HTML content including Calibri font
                      const htmlBlob = new Blob(
                        [
                          '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Formatted Text</title>',
                          '<style>body, * { font-family: Calibri, "Segoe UI", Roboto, sans-serif !important; }</style>',
                          '</head><body>',
                          htmlContent,
                          '</body></html>',
                        ],
                        { type: 'text/html' }
                      )

                      // Create a plain text version as fallback
                      const textBlob = new Blob([updatedText], { type: 'text/plain' })

                      // Try to use the modern clipboard API if available
                      if (navigator.clipboard && navigator.clipboard.write) {
                        navigator.clipboard.write([
                          new ClipboardItem({
                            'text/html': htmlBlob,
                            'text/plain': textBlob,
                          }),
                        ])
                      } else {
                        toast.warning(
                          "Votre navigateur ne supporte pas l'API de presse-papiers modernisée. Le texte brut sera copié à la place."
                        )
                      }
                      // Clean up
                      document.body.removeChild(tempElement)
                    } else {
                      // Fallback to plain text
                      navigator.clipboard.writeText(updatedText)
                    }
                  } catch (error) {
                    console.error('Error copying formatted text:', error)
                    // Fallback to plain text
                    navigator.clipboard.writeText(updatedText)
                  }
                  toast.success('Texte copié dans le presse-papier')
                }}
                style={{ marginTop: 8 }}
              >
                Copier le texte
              </Button>

              <Typography variant="subtitle1" style={{ marginTop: 16 }} gutterBottom>
                Correspondance des variables:
              </Typography>
              <Paper style={{ maxHeight: 200, overflow: 'auto', padding: 8 }}>
                <List dense>
                  {Object.entries(variableMapping).map(([oldId, newId], index) => {
                    const variable = extractedVariables.find((v) => v.id === oldId)
                    return (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${variable?.type}_${oldId} → ${variable?.type}_${newId}`}
                        />
                      </ListItem>
                    )
                  })}
                </List>
              </Paper>
            </>
          )}

          {!importComplete && (
            <Button
              variant="contained"
              color="primary"
              onClick={extractVariables}
              disabled={isExtracting || !textContent.trim()}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              {isExtracting ? 'Extraction...' : 'Extraire'}
            </Button>
          )}

          {extractedVariables.length > 0 && !importComplete && (
            <Paper style={{ maxHeight: 300, overflow: 'auto', marginTop: 16, padding: 16 }}>
              <Typography variant="subtitle1" gutterBottom>
                Variables trouvées ({extractedVariables.length})
                <Button size="small" onClick={handleSelectAll} style={{ marginLeft: 16 }}>
                  {selectedVariableIds.length === extractedVariables.length
                    ? 'Désélectionner tout'
                    : 'Sélectionner tout'}
                </Button>
              </Typography>

              <List dense>
                {extractedVariables.map((variable, index) => (
                  <ListItem key={index} button onClick={() => handleToggleVariable(variable)}>
                    <Checkbox
                      edge="start"
                      checked={selectedVariableIds.includes(variable.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText primary={`Type: ${variable.type}, ID: ${variable.id}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {importComplete ? 'Fermer' : 'Annuler'}
          </Button>
          {!importComplete && (
            <Button
              onClick={importVariables}
              color="primary"
              variant="contained"
              disabled={isImporting || selectedVariableIds.length === 0}
            >
              {isImporting ? 'Importation...' : 'Importer les variables sélectionnées'}
            </Button>
          )}
          {importComplete && (
            <Button onClick={handleClickOpen} color="primary" variant="contained">
              Nouvelle importation
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
